import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { RiDeleteBinLine, RiFileCopyLine, RiPencilLine } from 'react-icons/ri';
import {
  WorkflowIcon,
  WorkflowIconPicker,
} from './subcomponents/WorkflowIconPicker';


function WorkflowList({
  workflows,
  loadingWorkflows,
  activeWorkflowId,
  activeRuns,
  // Set of workflow IDs with a server-side run in progress (running in any
  // tab/session), so the dot reflects server truth, not just local streams.
  runningWorkflowIds,
  onSelect,
  onNew,
  onDelete,
  onRename,
  // Creation form state is lifted to the parent so the canvas "+ New
  // Workflow" button and the sidebar "+ New" button share the same form.
  showNewForm,
  setShowNewForm,
  isMobile,
  // When set, automatically opens the new-workflow form pre-filled with
  // copy data (name/desc/dag_json) from the canvas copy button.
  copyPrefill,
  onCopyPrefillConsumed,
}) {
  // New-workflow creation form state
  const [newWfName, setNewWfName] = useState('');
  const [newWfDesc, setNewWfDesc] = useState('');
  const [newWfIcon, setNewWfIcon] = useState(null);
  const [nameError, setNameError] = useState('');
  const [descError, setDescError] = useState('');
  const [copyDagJson, setCopyDagJson] = useState(null);

  // Edit modal state
  const [editingWf, setEditingWf] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editError, setEditError] = useState('');

  const startEdit = useCallback((wf, e) => {
    e.stopPropagation();
    setEditingWf(wf);
    setEditName(wf.name);
    setEditDesc(wf.description || '');
    setEditError('');
  }, []);

  const commitEdit = useCallback(() => {
    const name = editName.trim();
    const desc = editDesc.trim();
    if (!name) { setEditError('Name is required.'); return; }
    if (!desc) { setEditError('Description is required.'); return; }
    if (onRename) onRename(editingWf.id, name, desc);
    setEditingWf(null);
  }, [editName, editDesc, editingWf, onRename]);

  // When a canvas-level copy button pre-fills this form, apply the data.
  useEffect(() => {
    if (!copyPrefill) return;
    setNewWfName(copyPrefill.name);
    setNewWfDesc(copyPrefill.desc);
    setCopyDagJson(copyPrefill.dagJson);
    setNameError('');
    setDescError('');
    setShowNewForm(true);
    onCopyPrefillConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyPrefill]);

  // Open the creation form blank
  const handleOpenNewForm = useCallback(() => {
    setNewWfName('');
    setNewWfDesc('');
    setNewWfIcon(null);
    setCopyDagJson(null);
    setNameError('');
    setDescError('');
    setShowNewForm(true);
  }, [setShowNewForm]);

  // Open the creation form pre-filled with the source workflow's data
  const handleOpenCopyForm = useCallback((wf, e) => {
    e.stopPropagation();
    setNewWfName(`${wf.name} (copy)`);
    setNewWfDesc(wf.description || '');
    setNewWfIcon(null);
    setCopyDagJson(wf.dag_json);
    setNameError('');
    setDescError('');
    setShowNewForm(true);
  }, [setShowNewForm]);

  const handleCreate = useCallback(() => {
    const name = newWfName.trim();
    const desc = newWfDesc.trim();
    if (!name) { setNameError('A name is required.'); return; }
    if (!desc) { setDescError('A description is required.'); return; }
    if (!newWfIcon) return;
    onNew(name, desc, newWfIcon, copyDagJson);
    setShowNewForm(false);
    setCopyDagJson(null);
  }, [newWfName, newWfDesc, newWfIcon, copyDagJson, onNew, setShowNewForm]);

  const handleCancelNew = useCallback(() => {
    setShowNewForm(false);
    setCopyDagJson(null);
    setNameError('');
    setDescError('');
  }, [setShowNewForm]);

  // Split into platform defaults and user workflows
  const defaultWorkflows = workflows.filter((wf) => wf.is_default);
  const userWorkflows = workflows.filter((wf) => !wf.is_default);

  // Icons already used in My Workflows — cannot be reused within that section
  const usedUserIcons = useMemo(
    () => userWorkflows.map((wf) => wf.icon).filter(Boolean),
    [userWorkflows],
  );

  // Native non-passive touchend handler for iOS Safari.
  // iOS Safari does not reliably fire `click` on the first tap for divs
  // inside scrollable containers, even with `cursor: pointer` and
  // `touch-action: manipulation`.  A non-passive touchend listener lets us
  // call preventDefault(), which suppresses the browser-synthesised click
  // and instead calls onSelect directly — giving single-tap selection.
  const listRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);
  const touchStartYRef = useRef(0);

  useEffect(() => {
    if (!isMobile) return;
    const el = listRef.current;
    if (!el) return;

    const handleTouchStart = (e) => {
      touchStartYRef.current = e.touches[0].clientY;
    };
    const handleTouchEnd = (e) => {
      const dy = Math.abs(
        e.changedTouches[0].clientY - touchStartYRef.current,
      );
      if (dy > 10) return; // scroll gesture — let the browser handle it
      // Don't intercept taps on action buttons or their container
      if (e.target.closest(
        '.wf-workflow-actions, .wf-workflow-rename-btn, .wf-workflow-edit-btn, .wf-workflow-delete-btn',
      )) return;
      const item = e.target.closest('[data-wf-id]');
      if (!item) return;
      e.preventDefault(); // suppress the delayed synthesised click
      onSelectRef.current(item.dataset.wfId);
    };

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile]);

  const canCreate = newWfName.trim().length > 0
    && newWfDesc.trim().length > 0
    && newWfIcon !== null;

  const renderItem = (wf) => {
    const wfRun = activeRuns?.[wf.id];
    // Running if this tab is streaming a run OR the server reports one in
    // progress (e.g. started in another tab/session, or survived a reload).
    const wfRunning =
      (wfRun && ['pending', 'running'].includes(wfRun.status)) ||
      !!runningWorkflowIds?.has(wf.id);
    const wfDone =
      !wfRunning &&
      wfRun &&
      ['completed', 'failed', 'cancelled'].includes(wfRun.status);

    return (
      <div
        key={wf.id}
        data-wf-id={wf.id}
        className={`wf-workflow-item${wf.id === activeWorkflowId ? ' active' : ''}`}
        onClick={() => onSelect(wf.id)}
      >
        <>
            {/* Workflow icon */}
            <span
              style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
              className="wf-workflow-item-icon"
            >
              <WorkflowIcon
                iconKey={wf.icon}
                size={14}
                color={wf.id === activeWorkflowId
                  ? 'var(--primary-color, #636cff)'
                  : 'var(--text-secondary)'}
              />
            </span>
            {/* Name + description — hidden on mobile via CSS */}
            <span
              className="wf-workflow-item-name"
              style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}
            >
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {wf.name}
              </span>
              {wf.description && (
                <span style={{
                  fontSize: '0.65rem',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontWeight: 400,
                  marginTop: 1,
                }}>
                  {wf.description}
                </span>
              )}
            </span>
            {/* Status indicators */}
            {wfRunning && (
              <span
                title="Run in progress"
                style={{
                  display: 'inline-block',
                  width: 7, height: 7,
                  borderRadius: '50%',
                  background: 'var(--accent-color, #3b82f6)',
                  flexShrink: 0,
                  animation: 'wf-pulse 1.4s ease-in-out infinite',
                }}
              />
            )}
            {wfDone && !wfRunning && (
              <span
                title={`Last run: ${wfRun.status}`}
                style={{
                  fontSize: '0.6rem',
                  color: wfRun.status === 'completed'
                    ? 'var(--success-text-color, #065f46)'
                    : 'var(--error-text-color, #dc2626)',
                  flexShrink: 0,
                }}
              >
                {wfRun.status === 'completed' ? '✓' : '✗'}
              </span>
            )}
            {/* Action buttons — copy for all; edit/delete for user workflows */}
            <div className="wf-workflow-actions">
              <button
                className="wf-workflow-rename-btn"
                title={`Duplicate "${wf.name}"`}
                onClick={(e) => handleOpenCopyForm(wf, e)}
                aria-label={`Copy ${wf.name}`}
              >
                <RiFileCopyLine size={12} />
              </button>
              {!wf.is_default && (
                <button
                  className="wf-workflow-edit-btn"
                  title="Edit"
                  onClick={(e) => startEdit(wf, e)}
                  aria-label={`Edit ${wf.name}`}
                >
                  <RiPencilLine size={12} />
                </button>
              )}
              {!wf.is_default && (
                <button
                  className="wf-workflow-delete-btn"
                  title="Delete"
                  onClick={(e) => { e.stopPropagation(); onDelete(wf.id); }}
                >
                  <RiDeleteBinLine size={12} />
                </button>
              )}
            </div>
          </>
      </div>
    );
  };

  return (
    <>
      <div ref={listRef}>
      {/* ── Platform Workflows ── */}
      {defaultWorkflows.length > 0 && (
        <>
          <div className="wf-sidebar-section" style={{ fontSize: '0.68rem' }}>
            Platform Workflows
          </div>
          <div className="wf-workflow-list">
            {defaultWorkflows.map(renderItem)}
          </div>
        </>
      )}

      {/* ── My Workflows ── */}
      <div className="wf-sidebar-section" style={{ fontSize: '0.68rem' }}>
        My Workflows
        {!showNewForm && (
          <button
            className="wf-btn"
            onClick={handleOpenNewForm}
            style={{ marginLeft: 'auto', padding: '2px 8px', fontSize: '0.78rem', float: 'right' }}
          >
            + New
          </button>
        )}
      </div>

      <div className="wf-workflow-list">
        {loadingWorkflows && (
          <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        )}

        {/* Inline creation form — shared by "+ New" and copy actions */}
        {showNewForm && (
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border-color)' }}>
            {copyDagJson && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 4, fontStyle: 'italic' }}>
                Duplicating workflow — choose a new name and icon
              </div>
            )}
            {/* Name field */}
            <input
              className="wf-rename-input"
              value={newWfName}
              onChange={(e) => { setNewWfName(e.target.value); setNameError(''); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') handleCancelNew();
              }}
              placeholder="Workflow name (required)"
              autoFocus
              style={{
                width: '100%',
                marginBottom: nameError ? 2 : 6,
                borderColor: nameError ? 'var(--error-text-color, #dc2626)' : undefined,
              }}
            />
            {nameError && (
              <div style={{ fontSize: '0.72rem', color: 'var(--error-text-color, #dc2626)', marginBottom: 4 }}>
                {nameError}
              </div>
            )}
            {/* Description field */}
            <textarea
              className="wf-rename-input"
              value={newWfDesc}
              onChange={(e) => { setNewWfDesc(e.target.value); setDescError(''); }}
              placeholder="Short description (required)"
              rows={2}
              style={{
                width: '100%',
                marginBottom: descError ? 2 : 6,
                resize: 'vertical',
                fontFamily: 'inherit',
                fontSize: '0.82rem',
                borderColor: descError ? 'var(--error-text-color, #dc2626)' : undefined,
              }}
            />
            {descError && (
              <div style={{ fontSize: '0.72rem', color: 'var(--error-text-color, #dc2626)', marginBottom: 4 }}>
                {descError}
              </div>
            )}
            {/* Icon picker — already-used icons in My Workflows are disabled */}
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: 3 }}>
              Select an icon{!newWfIcon && <span style={{ color: 'var(--error-text-color, #dc2626)' }}> (required)</span>}
            </div>
            <WorkflowIconPicker
              value={newWfIcon}
              onChange={setNewWfIcon}
              usedIcons={usedUserIcons}
            />
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <button
                className="wf-btn wf-btn-primary"
                onClick={handleCreate}
                disabled={!canCreate}
                title={!canCreate ? 'Enter a name, description, and icon to continue' : undefined}
                style={{
                  flex: 1, padding: '4px 0', fontSize: '0.78rem',
                  opacity: canCreate ? 1 : 0.5,
                }}
              >
                Create
              </button>
              <button
                className="wf-btn"
                onClick={handleCancelNew}
                style={{ flex: 1, padding: '4px 0', fontSize: '0.78rem' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {!loadingWorkflows && userWorkflows.length === 0 && !showNewForm && (
          <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
            No custom workflows yet.
          </div>
        )}

        {userWorkflows.map(renderItem)}
      </div>
      </div>{/* end listRef */}

      {/* ── Edit workflow modal — portalled to document.body to escape
           any ancestor overflow/stacking-context constraints ── */}
      {editingWf && createPortal(
        <div
          className="wf-modal-backdrop"
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.45)',
            zIndex: 3000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: 'clamp(20px, 8vh, 80px) 20px 20px',
            overflowY: 'auto',
          }}
          onClick={() => setEditingWf(null)}
        >
          <div
            className="wf-modal-card"
            style={{
              background: 'var(--modal-background)',
              border: '1px solid var(--border-color)',
              borderRadius: 10,
              padding: '20px',
              width: '100%', maxWidth: 360,
              boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 14 }}>
              Edit Workflow
            </div>
            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              Name <span style={{ color: 'var(--error-text-color, #dc2626)' }}>*</span>
            </label>
            <input
              className="wf-rename-input"
              value={editName}
              onChange={(e) => { setEditName(e.target.value); setEditError(''); }}
              placeholder="Workflow name"
              autoFocus={!isMobile}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditingWf(null); }}
              style={{ width: '100%', marginBottom: 10 }}
            />
            <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>
              Description <span style={{ color: 'var(--error-text-color, #dc2626)' }}>*</span>
            </label>
            <textarea
              className="wf-rename-input"
              value={editDesc}
              onChange={(e) => { setEditDesc(e.target.value); setEditError(''); }}
              placeholder="Short description of what this workflow does. We recommend providing an example of how the workflow runs."
              rows={3}
              style={{
                width: '100%', marginBottom: 6,
                resize: 'vertical', fontFamily: 'inherit', fontSize: '0.82rem',
              }}
            />
            {editError && (
              <div style={{ fontSize: '0.72rem', color: 'var(--error-text-color, #dc2626)', marginBottom: 8 }}>
                {editError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button className="wf-btn" onClick={() => setEditingWf(null)}>
                Cancel
              </button>
              <button className="wf-btn wf-btn-primary" onClick={commitEdit}>
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}


export { WorkflowList };
